import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { insightsAPI, vehiclesAPI, adminUtilityAPI, feedbackAPI, emailTemplatesAPI, userActivityAPI } from '../services/api';
import FeedManagement from './FeedManagement';
import { formatDateBritish, formatDateTimeBritish } from '../utils/dateUtils';
import './VehicleList.css';

interface Client {
  id: number;
  companyName: string;
}

interface FeedbackEntry {
  id: number;
  category: string;
  message: string;
  submittedAt?: string;
  createdAt?: string;
  submittedByUsername?: string;
  username?: string;
  isRead: boolean;
  clientId?: number | null;
  clientName?: string | null;
}

interface Vehicle {
  id: number;
  registration: string;
  make: string;
  model: string;
  vehicleType: string | null;
  inceptionDate: string | null;
  coverStatus: string;
}

interface EmailTemplate {
  id: number; templateKey: string; name: string; description: string;
  subjectTemplate: string; bodyHtml: string;
  updatedAt?: string; updatedBy?: string;
}

// ── Template grouping ────────────────────────────────────────────────────────
const TEMPLATE_GROUPS: { label: string; keys: string[] }[] = [
  { label: 'Vehicle Addition',  keys: ['vehicle-add-submitted',       'vehicle-add-approved',       'vehicle-add-rejected'] },
  { label: 'Vehicle Removal',   keys: ['vehicle-remove-submitted',    'vehicle-remove-approved',    'vehicle-remove-rejected'] },
  { label: 'Temporary Cover',   keys: ['temp-cover-submitted',        'temp-cover-approved',        'temp-cover-rejected'] },
  { label: 'Reinstatement',     keys: ['reinstatement-submitted',     'reinstatement-approved',     'reinstatement-rejected'] },
  { label: 'Change of Vehicle', keys: ['change-of-vehicle-submitted', 'change-of-vehicle-approved', 'change-of-vehicle-rejected'] },
  { label: 'Bulk Actions',      keys: ['bulk-approved',               'bulk-rejected'] },
];

const PLACEHOLDERS = [
  { token: '{{ClientName}}',     desc: 'Client company name' },
  { token: '{{ActionType}}',     desc: 'Request type (e.g. Add Vehicle Request)' },
  { token: '{{RequestedBy}}',    desc: 'User who submitted the request' },
  { token: '{{ApprovedBy}}',     desc: 'Admin who approved / processed' },
  { token: '{{ProcessedBy}}',    desc: 'Admin who processed the decision' },
  { token: '{{Decision}}',       desc: '"Approved" or "Rejected"' },
  { token: '{{Notes}}',          desc: 'Additional notes (HTML block)' },
  { token: '{{ActionDate}}',     desc: 'Date and time of action (dd/MM/yyyy HH:mm)' },
  { token: '{{VehicleDetails}}', desc: 'HTML table of vehicle details' },
  { token: '{{Registration}}',   desc: 'Vehicle registration plate' },
  { token: '{{Make}}',           desc: 'Vehicle make' },
  { token: '{{Model}}',          desc: 'Vehicle model' },
  { token: '{{VehicleType}}',    desc: 'Vehicle type' },
  { token: '{{PortalUrl}}',      desc: 'Myshield Environment URL' },
  { token: '{{VehicleCount}}',   desc: 'Number of vehicles (bulk emails)' },
  { token: '{{VehicleList}}',    desc: 'HTML list of vehicles (bulk emails)' },
  { token: '{{Reason}}',         desc: 'Rejection reason (bulk / rejection emails)' },
];

// ── User-activity types ──────────────────────────────────────────────────────
interface ActivityStats {
  loginsToday: number;
  logoutsToday: number;
  logins30d: number;
  uniqueUsers30d: number;
  auditActions30d: number;
  workstreamDecisions30d: number;
}

interface UserSummary {
  userId: number;
  username: string;
  fullName: string;
  email: string;
  role: string;
  clientName?: string;
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
  totalLogins: number;
  recentLogins: number;
  totalActions: number;
  recentActions: number;
}

interface ActivityEvent {
  timestamp: string;
  category: string;
  eventType: string;
  username?: string;
  fullName?: string;
  role?: string;
  clientName?: string;
  description?: string;
  entityId?: number;
  entityDescription?: string;
  ipAddress?: string;
}

const CATEGORIES = ['All', 'Authentication', 'Vehicle', 'Driver', 'Workstream'];

const EVENT_COLOURS: Record<string, { bg: string; color: string }> = {
  Login:                       { bg: '#dcfce7', color: '#166534' },
  Logout:                      { bg: '#f1f5f9', color: '#475569' },
  Create:                      { bg: '#dbeafe', color: '#1e40af' },
  Update:                      { bg: '#fef9c3', color: '#854d0e' },
  Delete:                      { bg: '#fee2e2', color: '#991b1b' },
  'Workstream.Approved':       { bg: '#dcfce7', color: '#166534' },
  'Workstream.Rejected':       { bg: '#fee2e2', color: '#991b1b' },
  'Workstream.RemovalRequest': { bg: '#fef3c7', color: '#92400e' },
  'Workstream.RemovalApproved':{ bg: '#dcfce7', color: '#166534' },
  'Workstream.RemovalRejected':{ bg: '#fee2e2', color: '#991b1b' },
  'Workstream.ReinstatementRequest': { bg: '#ede9fe', color: '#4c1d95' },
  'Workstream.ReinstatementApproved':{ bg: '#dcfce7', color: '#166534' },
};

function eventBadge(eventType: string) {
  const style = EVENT_COLOURS[eventType] ?? { bg: '#f3f4f6', color: '#374151' };
  const label = eventType.includes('.') ? eventType.split('.').pop()! : eventType;
  return (
    <span style={{
      ...style,
      padding: '2px 8px', borderRadius: 9999,
      fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' as const,
    }}>{label}</span>
  );
}

const AdminTools: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'inception' | 'feedback' | 'scripts' | 'user-activity' | 'feed'>('inception');

  const [clients,          setClients]          = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [vehicles,         setVehicles]         = useState<Vehicle[]>([]);
  const [loadingVehicles,  setLoadingVehicles]  = useState(false);
  const [selectedIds,      setSelectedIds]      = useState<Set<number>>(new Set());
  const [inceptionDate,    setInceptionDate]    = useState<string>('');
  const [showBlankOnly,    setShowBlankOnly]    = useState<boolean>(false);
  const [confirming,       setConfirming]       = useState(false);
  const [updating,         setUpdating]         = useState(false);
  const [error,            setError]            = useState<string | null>(null);
  const [successMsg,       setSuccessMsg]       = useState<string | null>(null);

  // Feedback state
  const [feedbackItems,       setFeedbackItems]       = useState<FeedbackEntry[]>([]);
  const [feedbackLoading,     setFeedbackLoading]     = useState(false);
  const [feedbackFilter,      setFeedbackFilter]      = useState<'all' | 'pending' | 'actioned'>('all');
  const [feedbackSearch,      setFeedbackSearch]      = useState('');
  const [selectedFeedbackIds, setSelectedFeedbackIds] = useState<Set<number>>(new Set());
  const [actioningFeedback,   setActioningFeedback]   = useState(false);

  // ── User Activity state ────────────────────────────────────────────────────
  const [actStats,      setActStats]      = useState<ActivityStats | null>(null);
  const [actSummary,    setActSummary]    = useState<UserSummary[]>([]);
  const [actTimeline,   setActTimeline]   = useState<ActivityEvent[]>([]);
  const [actTotal,      setActTotal]      = useState(0);
  const [actPage,       setActPage]       = useState(1);
  const [actTotalPages, setActTotalPages] = useState(1);
  const [actLoading,    setActLoading]    = useState(false);
  const [actStatsLoading, setActStatsLoading] = useState(false);
  const [actFilterUser,   setActFilterUser]   = useState('');
  const [actFilterCat,    setActFilterCat]    = useState('All');
  const [actFilterStart,  setActFilterStart]  = useState('');
  const [actFilterEnd,    setActFilterEnd]    = useState('');
  const [actSubView,      setActSubView]      = useState<'timeline' | 'summary'>('timeline');
  const [actExporting,    setActExporting]    = useState(false);

  // Email Scripts state
  const [emailTemplates,   setEmailTemplates]   = useState<EmailTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [expandedKey,      setExpandedKey]      = useState<string | null>(null);
  const [drafts,           setDrafts]           = useState<Record<string, { subject: string; body: string }>>({});
  const [savingKey,        setSavingKey]        = useState<string | null>(null);
  const [templateMsg,      setTemplateMsg]      = useState<{ key: string; ok: boolean; text: string } | null>(null);

  const loadFeedback = useCallback(async () => {
    setFeedbackLoading(true);
    try {
      const res = await feedbackAPI.getAll();
      setFeedbackItems(Array.isArray(res.data) ? res.data : []);
    } catch { /* non-critical */ } finally { setFeedbackLoading(false); }
  }, []);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const res = await emailTemplatesAPI.getAll();
      const data: EmailTemplate[] = Array.isArray(res.data) ? res.data : [];
      setEmailTemplates(data);
      setDrafts((prev) => {
        const next = { ...prev };
        data.forEach((t) => {
          if (!next[t.templateKey]) {
            next[t.templateKey] = { subject: t.subjectTemplate, body: t.bodyHtml };
          }
        });
        return next;
      });
    } catch { /* non-critical */ } finally { setTemplatesLoading(false); }
  }, []);

  const handleBulkAction = async () => {
    if (selectedFeedbackIds.size === 0) return;
    setActioningFeedback(true);
    try {
      await Promise.all(Array.from(selectedFeedbackIds).map((id) => feedbackAPI.markRead(id)));
      setFeedbackItems((prev) =>
        prev.map((f) => selectedFeedbackIds.has(f.id) ? { ...f, isRead: true } : f)
      );
      setSelectedFeedbackIds(new Set());
    } catch {
      // non-critical
    } finally {
      setActioningFeedback(false);
    }
  };

  const toggleFeedbackRow = (id: number) =>
    setSelectedFeedbackIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const selectAllFeedback  = () => setSelectedFeedbackIds(new Set(displayFeedback.map((f) => f.id)));
  const selectNoneFeedback = () => setSelectedFeedbackIds(new Set());

  // ── User Activity loaders ──────────────────────────────────────────────────
  const loadActStats = useCallback(async () => {
    setActStatsLoading(true);
    try {
      const r = await userActivityAPI.getStats();
      setActStats(r.data);
    } catch { /* non-critical */ } finally { setActStatsLoading(false); }
  }, []);

  const loadActSummary = useCallback(async () => {
    setActLoading(true);
    try {
      const r = await userActivityAPI.getSummary();
      setActSummary(Array.isArray(r.data) ? r.data : []);
    } catch { /* non-critical */ } finally { setActLoading(false); }
  }, []);

  const loadActTimeline = useCallback(async (page = 1) => {
    setActLoading(true);
    try {
      const r = await userActivityAPI.getTimeline({
        username:  actFilterUser  || undefined,
        category:  actFilterCat !== 'All' ? actFilterCat : undefined,
        startDate: actFilterStart || undefined,
        endDate:   actFilterEnd   || undefined,
        page,
        pageSize: 50,
      });
      setActTimeline(r.data.events ?? []);
      setActTotal(r.data.total ?? 0);
      setActPage(r.data.page ?? 1);
      setActTotalPages(r.data.totalPages ?? 1);
    } catch { /* non-critical */ } finally { setActLoading(false); }
  }, [actFilterUser, actFilterCat, actFilterStart, actFilterEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Excel exports ────────────────────────────────────────────────────────────
  const stamp = () => {
    const now = new Date();
    return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
  };

  const roleLabel = (r: string) => {
    const m: Record<string, string> = {
      SuperUser: 'Super User', SuperAdmin: 'Super User',
      FleetManager: 'Fleet Manager', Admin: 'Admin', MTA: 'MTA', Client: 'Client',
    };
    return m[r] ?? r;
  };

  // Download Activity Timeline only
  const exportTimelineToExcel = useCallback(async () => {
    setActExporting(true);
    try {
      const timelineRes = await userActivityAPI.getTimeline({
        username:  actFilterUser  || undefined,
        category:  actFilterCat !== 'All' ? actFilterCat : undefined,
        startDate: actFilterStart || undefined,
        endDate:   actFilterEnd   || undefined,
        page: 1,
        pageSize: 10000,
      });
      const allEvents: ActivityEvent[] = timelineRes.data.events ?? [];
      const rows = allEvents.map((ev) => ({
        'Timestamp':    formatDateTimeBritish(ev.timestamp),
        'Category':     ev.category,
        'Action':       ev.eventType.includes('.') ? ev.eventType.split('.').pop()! : ev.eventType,
        'Username':     ev.username ?? '',
        'Full Name':    ev.fullName  ?? '',
        'Role':         ev.role ? roleLabel(ev.role) : '',
        'Client':       ev.clientName      ?? '',
        'Description':  ev.description     ?? '',
        'Subject':      ev.entityDescription ?? '',
        'IP Address':   ev.ipAddress       ?? '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [
        { wch: 20 }, { wch: 16 }, { wch: 24 }, { wch: 18 }, { wch: 22 },
        { wch: 14 }, { wch: 24 }, { wch: 50 }, { wch: 34 }, { wch: 16 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Activity Timeline');
      XLSX.writeFile(wb, `Activity_Timeline_${stamp()}.xlsx`);
    } catch (e) {
      console.error('Export failed', e);
    } finally {
      setActExporting(false);
    }
  }, [actFilterUser, actFilterCat, actFilterStart, actFilterEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  // Download Per-User Summary only
  const exportSummaryToExcel = useCallback(async () => {
    setActExporting(true);
    try {
      let summary = actSummary;
      if (summary.length === 0) {
        const sr = await userActivityAPI.getSummary();
        summary = Array.isArray(sr.data) ? sr.data : [];
      }
      const rows = summary.map((u) => ({
        'Username':        u.username,
        'Full Name':       u.fullName || '',
        'Email':           u.email,
        'Role':            roleLabel(u.role),
        'Company':         u.clientName ?? '',
        'Status':          u.isActive ? 'Active' : 'Inactive',
        'Account Created': formatDateBritish(u.createdAt),
        'Last Login':      u.lastLoginAt ? formatDateTimeBritish(u.lastLoginAt) : 'Never',
        'Total Logins':    u.totalLogins,
        'Logins (30d)':    u.recentLogins,
        'Total Actions':   u.totalActions,
        'Actions (30d)':   u.recentActions,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [
        { wch: 18 }, { wch: 22 }, { wch: 30 }, { wch: 14 }, { wch: 24 },
        { wch: 10 }, { wch: 18 }, { wch: 22 }, { wch: 14 }, { wch: 14 },
        { wch: 16 }, { wch: 16 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'User Summary');
      XLSX.writeFile(wb, `User_Summary_${stamp()}.xlsx`);
    } catch (e) {
      console.error('Export failed', e);
    } finally {
      setActExporting(false);
    }
  }, [actSummary]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load active clients on mount
  useEffect(() => {
    insightsAPI.getClientsList()
      .then((r) => setClients(r.data))
      .catch(() => setError('Failed to load clients.'));
    loadFeedback();
    loadTemplates();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load user-activity data when the tab is first opened
  useEffect(() => {
    if (activeTab === 'user-activity') {
      loadActStats();
      if (actSubView === 'summary') loadActSummary();
      else loadActTimeline(1);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load vehicles when client changes
  useEffect(() => {
    if (!selectedClientId) { setVehicles([]); setSelectedIds(new Set()); return; }
    setLoadingVehicles(true);
    setError(null);
    setSuccessMsg(null);
    vehiclesAPI.getByClient(parseInt(selectedClientId))
      .then((r) => {
        const data = r.data;
        setVehicles(Array.isArray(data) ? data : []);
        setSelectedIds(new Set());
      })
      .catch(() => setError('Failed to load vehicles for this client.'))
      .finally(() => setLoadingVehicles(false));
  }, [selectedClientId]);

  const displayVehicles = useMemo(
    () => showBlankOnly ? vehicles.filter((v) => !v.inceptionDate) : vehicles,
    [vehicles, showBlankOnly]
  );

  const blankCount = useMemo(() => vehicles.filter((v) => !v.inceptionDate).length, [vehicles]);

  const allVisibleSelected =
    displayVehicles.length > 0 && displayVehicles.every((v) => selectedIds.has(v.id));

  const toggleVehicle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setConfirming(false);
    setSuccessMsg(null);
  };

  const selectAll   = () => { setSelectedIds(new Set(displayVehicles.map((v) => v.id))); setConfirming(false); };
  const selectNone  = () => { setSelectedIds(new Set()); setConfirming(false); };
  const selectBlanks = () => {
    setSelectedIds(new Set(vehicles.filter((v) => !v.inceptionDate).map((v) => v.id)));
    setConfirming(false);
  };

  const handleUpdate = async () => {
    if (selectedIds.size === 0 || !inceptionDate) return;
    setUpdating(true);
    setError(null);
    try {
      const res = await adminUtilityAPI.bulkUpdateInceptionDates(
        Array.from(selectedIds),
        new Date(inceptionDate).toISOString()
      );
      setSuccessMsg(res.data.message);
      setSelectedIds(new Set());
      setConfirming(false);
      // Refresh vehicle list to show updated dates
      vehiclesAPI.getByClient(parseInt(selectedClientId))
        .then((r) => setVehicles(Array.isArray(r.data) ? r.data : []))
        .catch(() => {});
    } catch {
      setError('Update failed. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const canApply = selectedIds.size > 0 && !!inceptionDate;

  const displayFeedback = useMemo(() => {
    let items = feedbackItems;
    if (feedbackFilter === 'pending')  items = items.filter((f) => !f.isRead);
    if (feedbackFilter === 'actioned') items = items.filter((f) =>  f.isRead);
    if (feedbackSearch) {
      const s = feedbackSearch.toLowerCase();
      items = items.filter((f) =>
        f.message.toLowerCase().includes(s) ||
        f.category.toLowerCase().includes(s) ||
        (f.submittedByUsername ?? f.username ?? '').toLowerCase().includes(s) ||
        (f.clientName ?? '').toLowerCase().includes(s)
      );
    }
    return items;
  }, [feedbackItems, feedbackFilter, feedbackSearch]);

  const pendingCount = useMemo(() => feedbackItems.filter((f) => !f.isRead).length, [feedbackItems]);

  const allFeedbackSelected =
    displayFeedback.length > 0 && displayFeedback.every((f) => selectedFeedbackIds.has(f.id));

  const getFeedbackDate = (f: FeedbackEntry) => f.submittedAt || f.createdAt || '';

  // ── Email Scripts handlers ────────────────────────────────────────────────
  const templateByKey = useMemo(() => {
    const map: Record<string, EmailTemplate> = {};
    emailTemplates.forEach((t) => { map[t.templateKey] = t; });
    return map;
  }, [emailTemplates]);

  const handleExpandToggle = (key: string, t: EmailTemplate) => {
    if (expandedKey === key) { setExpandedKey(null); return; }
    setExpandedKey(key);
    setTemplateMsg(null);
    setDrafts((prev) => ({ ...prev, [key]: prev[key] ?? { subject: t.subjectTemplate, body: t.bodyHtml } }));
  };

  const handleSaveTemplate = async (t: EmailTemplate) => {
    const draft = drafts[t.templateKey];
    if (!draft) return;
    setSavingKey(t.templateKey);
    setTemplateMsg(null);
    try {
      await emailTemplatesAPI.update(t.id, { subjectTemplate: draft.subject, bodyHtml: draft.body });
      setEmailTemplates((prev) => prev.map((et) =>
        et.id === t.id ? { ...et, subjectTemplate: draft.subject, bodyHtml: draft.body, updatedAt: new Date().toISOString() } : et
      ));
      setTemplateMsg({ key: t.templateKey, ok: true, text: 'Saved successfully.' });
    } catch {
      setTemplateMsg({ key: t.templateKey, ok: false, text: 'Save failed. Please try again.' });
    } finally { setSavingKey(null); }
  };

  const handleResetTemplate = (t: EmailTemplate) => {
    setDrafts((prev) => ({ ...prev, [t.templateKey]: { subject: t.subjectTemplate, body: t.bodyHtml } }));
    setTemplateMsg(null);
  };

  const isDirty = (t: EmailTemplate) => {
    const d = drafts[t.templateKey];
    return !!d && (d.subject !== t.subjectTemplate || d.body !== t.bodyHtml);
  };

  // ── User-activity helpers ─────────────────────────────────────────────────
  const formatRole = (r: string) => {
    const m: Record<string, string> = {
      SuperUser: 'Super User', SuperAdmin: 'Super User',
      FleetManager: 'Fleet Mgr', Admin: 'Admin', MTA: 'MTA', Client: 'Client',
    };
    return m[r] ?? r;
  };

  const statCard = (label: string, value: number | string, sub?: string, colour = '#002060') => (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
      padding: '16px 20px', minWidth: 130, flex: '1 1 130px',
    }}>
      <div style={{ fontSize: 26, fontWeight: 700, color: colour }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
    </div>
  );

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '10px 22px', border: 'none', cursor: 'pointer',
    fontWeight: active ? 700 : 500, fontSize: 14,
    borderBottom: active ? '3px solid #002060' : '3px solid transparent',
    background: 'transparent', color: active ? '#002060' : '#6b7280',
    transition: 'all 0.15s',
  });

  return (
    <div className="vehicle-list">
      <div className="page-header"><h1>Admin Tools</h1></div>

      {/* Tab nav */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e5e7eb', marginBottom: 24 }}>
        <button style={tabStyle(activeTab === 'inception')} onClick={() => setActiveTab('inception')}>
          Bulk Inception Dates
        </button>
        <button style={tabStyle(activeTab === 'feedback')} onClick={() => setActiveTab('feedback')}>
          Feedback
          {pendingCount > 0 && (
            <span style={{ marginLeft: 6, background: '#ef4444', color: '#fff', borderRadius: 9999, fontSize: 11, fontWeight: 700, padding: '1px 6px' }}>
              {pendingCount}
            </span>
          )}
        </button>
        <button style={tabStyle(activeTab === 'scripts')} onClick={() => setActiveTab('scripts')}>
          Email Scripts
        </button>
        <button style={tabStyle(activeTab === 'user-activity')} onClick={() => setActiveTab('user-activity')}>
          User Activity
        </button>
        <button style={tabStyle(activeTab === 'feed')} onClick={() => setActiveTab('feed')}>
          My Feed
        </button>
      </div>

      {/* ── Tab: Bulk Update Inception Dates ── */}
      {activeTab === 'inception' && <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ marginBottom: 4 }}>Bulk Update Inception Dates</h2>
        <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>
          Select a client to see their vehicles, pick the ones you want to update, enter a date and apply.
        </p>

        {/* Step 1 — Client + Date */}
        <div className="form-row">
          <div className="form-group">
            <label>Client <span style={{ color: '#ef4444' }}>*</span></label>
            <select
              value={selectedClientId}
              onChange={(e) => { setSelectedClientId(e.target.value); setSuccessMsg(null); setError(null); }}
            >
              <option value="">— Select a client —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.companyName}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>New Inception Date <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              type="date"
              value={inceptionDate}
              onChange={(e) => { setInceptionDate(e.target.value); setConfirming(false); }}
            />
          </div>
        </div>

        {/* Feedback */}
        {error && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#b91c1c', fontSize: 14 }}>
            {error}
          </div>
        )}
        {successMsg && (
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, color: '#15803d', fontWeight: 600 }}>
            ✓ {successMsg}
          </div>
        )}

        {/* Step 2 — Vehicle list */}
        {loadingVehicles && (
          <p style={{ color: '#6b7280' }}>Loading vehicles…</p>
        )}

        {!loadingVehicles && vehicles.length > 0 && (
          <>
            {/* Toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>
                {vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''}
                {blankCount > 0 && (
                  <span style={{ color: '#f59e0b', marginLeft: 8 }}>
                    ({blankCount} without inception date)
                  </span>
                )}
              </span>

              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {blankCount > 0 && (
                  <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={selectBlanks}>
                    Select blanks ({blankCount})
                  </button>
                )}
                <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={selectAll}>
                  Select all
                </button>
                <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={selectNone}>
                  Select none
                </button>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={showBlankOnly}
                    onChange={(e) => setShowBlankOnly(e.target.checked)}
                    style={{ width: 14, height: 14 }}
                  />
                  Show blanks only
                </label>
              </div>
            </div>

            {/* Vehicle table */}
            <div style={{ overflowX: 'auto', marginBottom: 20 }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={allVisibleSelected ? selectNone : selectAll}
                        style={{ width: 15, height: 15, cursor: 'pointer' }}
                        title={allVisibleSelected ? 'Deselect all' : 'Select all visible'}
                      />
                    </th>
                    <th>Registration</th>
                    <th>Make / Model</th>
                    <th>Type</th>
                    <th>Cover Status</th>
                    <th>Current Inception Date</th>
                  </tr>
                </thead>
                <tbody>
                  {displayVehicles.map((v) => (
                    <tr
                      key={v.id}
                      onClick={() => toggleVehicle(v.id)}
                      style={{ cursor: 'pointer', background: selectedIds.has(v.id) ? '#eff6ff' : undefined }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(v.id)}
                          onChange={() => toggleVehicle(v.id)}
                          style={{ width: 15, height: 15, cursor: 'pointer' }}
                        />
                      </td>
                      <td><strong>{v.registration}</strong></td>
                      <td>{v.make} {v.model}</td>
                      <td>{v.vehicleType || '—'}</td>
                      <td>
                        <span style={{
                          color: v.coverStatus === 'Active' ? '#10b981'
                               : v.coverStatus === 'Pending Cover' ? '#f59e0b'
                               : '#6b7280',
                          fontWeight: 600
                        }}>
                          {v.coverStatus === 'Active' ? 'On Cover' : v.coverStatus}
                        </span>
                      </td>
                      <td>
                        {v.inceptionDate
                          ? formatDateBritish(v.inceptionDate)
                          : <span style={{ color: '#f59e0b', fontWeight: 600 }}>Not set</span>}
                      </td>
                    </tr>
                  ))}
                  {displayVehicles.length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }}>No vehicles to display.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Apply bar */}
            {selectedIds.size > 0 && (
              <div style={{ padding: '14px 18px', background: '#f0f9ff', border: '1px solid #7dd3fc', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600 }}>
                  {selectedIds.size} vehicle{selectedIds.size !== 1 ? 's' : ''} selected
                </span>

                {!confirming ? (
                  <button
                    className="btn btn-primary"
                    onClick={() => setConfirming(true)}
                    disabled={!canApply}
                    title={!inceptionDate ? 'Enter a date first' : undefined}
                  >
                    Apply Inception Date
                  </button>
                ) : (
                  <>
                    <span style={{ color: '#b45309', fontWeight: 600 }}>
                      Set to {new Date(inceptionDate).toLocaleDateString('en-GB')} for {selectedIds.size} vehicle{selectedIds.size !== 1 ? 's' : ''}?
                    </span>
                    <button
                      className="btn btn-primary"
                      onClick={handleUpdate}
                      disabled={updating}
                    >
                      {updating ? 'Updating…' : 'Confirm'}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setConfirming(false)}
                      disabled={updating}
                    >
                      Cancel
                    </button>
                  </>
                )}

                {!inceptionDate && (
                  <span style={{ color: '#6b7280', fontSize: 13 }}>← Enter a date above first</span>
                )}
              </div>
            )}
          </>
        )}

        {!loadingVehicles && selectedClientId && vehicles.length === 0 && (
          <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No active vehicles found for this client.</p>
        )}
      </div>}

      {/* ── Tab: Feedback ── */}
      {activeTab === 'feedback' && <div className="card" style={{ marginTop: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <h2 style={{ margin: 0 }}>Feedback Submissions</h2>
          {pendingCount > 0 && (
            <span style={{
              background: '#ef4444', color: '#fff', borderRadius: 9999,
              fontSize: 12, fontWeight: 700, padding: '2px 8px', lineHeight: '20px'
            }}>
              {pendingCount} pending
            </span>
          )}
        </div>
        <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
          User-submitted feedback from across the Environment. Select items and mark them as actioned once reviewed.
        </p>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '2 1 200px' }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Search</label>
            <input
              type="text"
              placeholder="Message, category, user..."
              value={feedbackSearch}
              onChange={(e) => setFeedbackSearch(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 140px' }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Status</label>
            <select
              value={feedbackFilter}
              onChange={(e) => setFeedbackFilter(e.target.value as 'all' | 'pending' | 'actioned')}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="actioned">Actioned</option>
            </select>
          </div>
          <button
            className="btn btn-secondary"
            onClick={loadFeedback}
            disabled={feedbackLoading}
            style={{ padding: '8px 16px', fontSize: 14 }}
          >
            {feedbackLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={allFeedbackSelected}
                    onChange={allFeedbackSelected ? selectNoneFeedback : selectAllFeedback}
                    style={{ width: 15, height: 15, cursor: 'pointer' }}
                    title={allFeedbackSelected ? 'Deselect all' : 'Select all visible'}
                  />
                </th>
                <th>Status</th>
                <th>Category</th>
                <th>Message</th>
                <th>Submitted By</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {feedbackLoading && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: '#6b7280', padding: '20px 0' }}>Loading...</td></tr>
              )}
              {!feedbackLoading && displayFeedback.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: '#9ca3af', fontStyle: 'italic', padding: '20px 0' }}>No feedback found.</td></tr>
              )}
              {!feedbackLoading && displayFeedback.map((f) => (
                <tr
                  key={f.id}
                  onClick={() => toggleFeedbackRow(f.id)}
                  style={{ cursor: 'pointer', background: selectedFeedbackIds.has(f.id) ? '#eff6ff' : f.isRead ? undefined : '#fffbeb' }}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedFeedbackIds.has(f.id)}
                      onChange={() => toggleFeedbackRow(f.id)}
                      style={{ width: 15, height: 15, cursor: 'pointer' }}
                    />
                  </td>
                  <td>
                    {f.isRead ? (
                      <span style={{ color: '#9ca3af', fontSize: 12 }}>Actioned</span>
                    ) : (
                      <span style={{
                        background: '#fef3c7', color: '#92400e',
                        padding: '2px 8px', borderRadius: 9999, fontSize: 12, fontWeight: 600
                      }}>Pending</span>
                    )}
                  </td>
                  <td>
                    <span style={{
                      background: '#e0f2fe', color: '#0369a1',
                      padding: '2px 8px', borderRadius: 9999, fontSize: 12, fontWeight: 600
                    }}>
                      {f.category}
                    </span>
                  </td>
                  <td style={{ fontSize: 13, maxWidth: 340 }}>
                    <span title={f.message}>
                      {f.message.length > 80 ? f.message.slice(0, 80) + '\u2026' : f.message}
                    </span>
                  </td>
                  <td style={{ fontSize: 13 }}>{f.submittedByUsername ?? f.username ?? '—'}</td>
                  <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                    {formatDateTimeBritish(getFeedbackDate(f)) || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Bulk action bar */}
        {selectedFeedbackIds.size > 0 && (
          <div style={{ marginTop: 14, padding: '14px 18px', background: '#f0f9ff', border: '1px solid #7dd3fc', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>
              {selectedFeedbackIds.size} item{selectedFeedbackIds.size !== 1 ? 's' : ''} selected
            </span>
            <button
              className="btn btn-primary"
              onClick={handleBulkAction}
              disabled={actioningFeedback}
            >
              {actioningFeedback ? 'Actioning...' : 'Mark as Actioned'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={selectNoneFeedback}
              disabled={actioningFeedback}
            >
              Cancel
            </button>
          </div>
        )}

        <div style={{ marginTop: 12, fontSize: 12, color: '#9ca3af' }}>
          Showing {displayFeedback.length} of {feedbackItems.length} submission{feedbackItems.length !== 1 ? 's' : ''}.
        </div>
      </div>}

      {/* ── Tab: Email Scripts ── */}
      {activeTab === 'scripts' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h2 style={{ margin: '0 0 4px' }}>Email Scripts</h2>
              <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>
                Edit the subject line and HTML body for each automated email. Use the placeholders below to insert dynamic content.
              </p>
            </div>
            <button className="btn btn-secondary" onClick={loadTemplates} disabled={templatesLoading} style={{ fontSize: 13 }}>
              {templatesLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {/* Placeholder reference */}
          <div className="card" style={{ marginBottom: 24, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: '0 0 10px', fontSize: 14, color: '#002060' }}>Available Placeholders</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px' }}>
              {PLACEHOLDERS.map((p) => (
                <div key={p.token} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <code style={{ background: '#e0e7ff', color: '#3730a3', padding: '2px 6px', borderRadius: 4, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{p.token}</code>
                  <span style={{ color: '#6b7280' }}>{p.desc}</span>
                </div>
              ))}
            </div>
          </div>

          {templatesLoading && <p style={{ color: '#6b7280' }}>Loading templates…</p>}

          {!templatesLoading && TEMPLATE_GROUPS.map((group) => {
            const groupTemplates = group.keys.map((k) => templateByKey[k]).filter(Boolean) as EmailTemplate[];
            if (groupTemplates.length === 0) return null;
            return (
              <div key={group.label} className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 12px', color: '#374151', fontSize: 15, borderBottom: '1px solid #f3f4f6', paddingBottom: 8 }}>
                  {group.label}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {groupTemplates.map((t) => {
                    const isExpanded = expandedKey === t.templateKey;
                    const dirty      = isDirty(t);
                    const draft      = drafts[t.templateKey] ?? { subject: t.subjectTemplate, body: t.bodyHtml };
                    const isSaving   = savingKey === t.templateKey;
                    const msg        = templateMsg?.key === t.templateKey ? templateMsg : null;
                    return (
                      <div key={t.templateKey} style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                        {/* Header row */}
                        <div
                          onClick={() => handleExpandToggle(t.templateKey, t)}
                          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: isExpanded ? '#f0f9ff' : '#fafafa', cursor: 'pointer', userSelect: 'none' }}
                        >
                          <span style={{ fontSize: 18, lineHeight: 1, color: '#6b7280' }}>{isExpanded ? '\u25be' : '\u25b8'}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{t.name}</div>
                            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{t.description}</div>
                          </div>
                          {dirty && (
                            <span style={{ fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 9999, fontWeight: 600 }}>Unsaved</span>
                          )}
                          {t.updatedAt && (
                            <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                              Last saved: {formatDateTimeBritish(t.updatedAt)}
                            </span>
                          )}
                        </div>
                        {/* Editor */}
                        {isExpanded && (
                          <div style={{ padding: '16px 16px 20px', borderTop: '1px solid #e5e7eb' }}>
                            <div style={{ marginBottom: 14 }}>
                              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 4 }}>Subject Line</label>
                              <input
                                type="text"
                                value={draft.subject}
                                onChange={(e) => setDrafts((prev) => ({ ...prev, [t.templateKey]: { ...prev[t.templateKey], subject: e.target.value } }))}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                              />
                            </div>
                            <div style={{ marginBottom: 16 }}>
                              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 8 }}>Email Body (HTML)</label>
                              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                                {/* Left: HTML editor */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, fontWeight: 500 }}>HTML SOURCE</div>
                                  <textarea
                                    value={draft.body}
                                    onChange={(e) => setDrafts((prev) => ({ ...prev, [t.templateKey]: { ...prev[t.templateKey], body: e.target.value } }))}
                                    rows={20}
                                    spellCheck={false}
                                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, fontFamily: 'Consolas, "Courier New", monospace', lineHeight: 1.5, boxSizing: 'border-box', resize: 'vertical' }}
                                  />
                                </div>
                                {/* Right: Live preview */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, fontWeight: 500 }}>LIVE PREVIEW</div>
                                  <iframe
                                    srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"/><style>body{margin:0;padding:12px;font-family:Arial,sans-serif;font-size:14px;background:#fff;}</style></head><body>${draft.body}</body></html>`}
                                    style={{ width: '100%', height: 420, border: '1px solid #d1d5db', borderRadius: 6, background: '#fff' }}
                                    sandbox="allow-same-origin"
                                    title="Email preview"
                                  />
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                              <button className="btn btn-primary" onClick={() => handleSaveTemplate(t)} disabled={isSaving || !dirty} style={{ fontSize: 13 }}>
                                {isSaving ? 'Saving\u2026' : 'Save Changes'}
                              </button>
                              {dirty && (
                                <button className="btn btn-secondary" onClick={() => handleResetTemplate(t)} disabled={isSaving} style={{ fontSize: 13 }}>Discard Changes</button>
                              )}
                              {msg && (
                                <span style={{ fontSize: 13, color: msg.ok ? '#15803d' : '#b91c1c', fontWeight: 600 }}>
                                  {msg.ok ? '\u2713' : '\u2717'} {msg.text}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {!templatesLoading && emailTemplates.length === 0 && (
            <div className="card" style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 20px' }}>
              No email templates found. They are seeded automatically on first startup.
            </div>
          )}
        </div>
      )}

      {/* ──────────────────── Tab: User Activity ──────────────────── */}
      {activeTab === 'user-activity' && (
        <div>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h2 style={{ margin: '0 0 4px' }}>User Activity Report</h2>
              <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>
                Full audit trail of every user action — logins, logouts, vehicle changes and workstream decisions.
              </p>
            </div>
            <button
              className="btn btn-secondary"
              style={{ fontSize: 13 }}
              onClick={() => { loadActStats(); if (actSubView === 'summary') loadActSummary(); else loadActTimeline(actPage); }}
              disabled={actLoading || actStatsLoading || actExporting}
            >
              {(actLoading || actStatsLoading) ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          {/* Stats strip */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
            {statCard('Logins today',        actStats?.loginsToday        ?? '—', undefined, '#166534')}
            {statCard('Logouts today',       actStats?.logoutsToday       ?? '—', undefined, '#475569')}
            {statCard('Logins (30 d)',       actStats?.logins30d          ?? '—', 'last 30 days', '#002060')}
            {statCard('Unique users (30 d)', actStats?.uniqueUsers30d     ?? '—', 'distinct logins', '#0369a1')}
            {statCard('Audit actions (30 d)', actStats?.auditActions30d    ?? '—', 'vehicle/driver changes', '#92400e')}
            {statCard('Workstream decisions (30 d)', actStats?.workstreamDecisions30d ?? '—', 'approvals & rejections', '#7c3aed')}
          </div>

          {/* Sub-view toggle */}
          <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e5e7eb', marginBottom: 20 }}>
            {(['timeline', 'summary'] as const).map((v) => (
              <button
                key={v}
                onClick={() => {
                  setActSubView(v);
                  if (v === 'summary') loadActSummary();
                  else loadActTimeline(1);
                }}
                style={{
                  padding: '8px 20px', border: 'none', cursor: 'pointer', fontSize: 13,
                  fontWeight: actSubView === v ? 700 : 500,
                  borderBottom: actSubView === v ? '3px solid #002060' : '3px solid transparent',
                  background: 'transparent',
                  color: actSubView === v ? '#002060' : '#6b7280',
                }}
              >
                {v === 'timeline' ? 'Activity Timeline' : 'Per-User Summary'}
              </button>
            ))}
          </div>

          {/* ── Activity Timeline ── */}
          {actSubView === 'timeline' && (
            <>
              {/* Filters */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16, alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '2 1 180px' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Username</label>
                  <input
                    type="text"
                    placeholder="Filter by username…"
                    value={actFilterUser}
                    onChange={(e) => setActFilterUser(e.target.value)}
                    style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13 }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 140px' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Category</label>
                  <select
                    value={actFilterCat}
                    onChange={(e) => setActFilterCat(e.target.value)}
                    style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13 }}
                  >
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 140px' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>From</label>
                  <input type="date" value={actFilterStart} onChange={(e) => setActFilterStart(e.target.value)}
                    style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13 }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 140px' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>To</label>
                  <input type="date" value={actFilterEnd} onChange={(e) => setActFilterEnd(e.target.value)}
                    style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13 }} />
                </div>
                <button className="btn btn-primary" style={{ fontSize: 13, padding: '7px 18px' }}
                  onClick={() => loadActTimeline(1)} disabled={actLoading}>
                  Search
                </button>
                <button className="btn btn-secondary" style={{ fontSize: 13, padding: '7px 14px' }}
                  onClick={() => {
                    setActFilterUser(''); setActFilterCat('All');
                    setActFilterStart(''); setActFilterEnd('');
                    setTimeout(() => loadActTimeline(1), 0);
                  }}>
                  Clear
                </button>
                <button
                  className="btn btn-primary"
                  style={{ fontSize: 13, padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 5, marginLeft: 'auto' }}
                  onClick={exportTimelineToExcel}
                  disabled={actExporting || actLoading}
                  title="Download Activity Timeline as Excel"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  {actExporting ? 'Preparing…' : 'Download Timeline'}
                </button>
              </div>

              {/* Results count */}
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 10 }}>
                {actLoading ? 'Loading…' : `${actTotal.toLocaleString()} events — page ${actPage} of ${actTotalPages}`}
              </div>

              {/* Timeline table */}
              <div className="card" style={{ marginBottom: 12, padding: 0, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: 155 }}>Timestamp</th>
                        <th style={{ width: 110 }}>Category</th>
                        <th style={{ width: 110 }}>Action</th>
                        <th>User</th>
                        <th>Description</th>
                        <th>Subject</th>
                        <th style={{ width: 110 }}>Client</th>
                        <th style={{ width: 105 }}>IP Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {actLoading && (
                        <tr><td colSpan={8} style={{ textAlign: 'center', color: '#6b7280', padding: 24 }}>Loading…</td></tr>
                      )}
                      {!actLoading && actTimeline.length === 0 && (
                        <tr><td colSpan={8} style={{ textAlign: 'center', color: '#9ca3af', fontStyle: 'italic', padding: 24 }}>No events found.</td></tr>
                      )}
                      {!actLoading && actTimeline.map((ev, i) => (
                        <tr key={i} style={{ fontSize: 13 }}>
                          <td style={{ whiteSpace: 'nowrap', color: '#6b7280' }}>{formatDateTimeBritish(ev.timestamp)}</td>
                          <td>
                            <span style={{ background: '#f1f5f9', color: '#334155', padding: '2px 7px', borderRadius: 9999, fontSize: 11, fontWeight: 600 }}>
                              {ev.category}
                            </span>
                          </td>
                          <td>{eventBadge(ev.eventType)}</td>
                          <td>
                            <div style={{ fontWeight: 600 }}>{ev.username ?? '—'}</div>
                            {ev.role && <div style={{ fontSize: 11, color: '#6b7280' }}>{formatRole(ev.role)}</div>}
                          </td>
                          <td style={{ maxWidth: 220 }}>
                            <span title={ev.description ?? ''}>
                              {(ev.description ?? '').length > 60 ? (ev.description ?? '').slice(0, 60) + '…' : (ev.description ?? '—')}
                            </span>
                          </td>
                          <td style={{ maxWidth: 180, color: '#374151' }}>
                            {ev.entityDescription
                              ? <span title={ev.entityDescription}>{ev.entityDescription.length > 35 ? ev.entityDescription.slice(0, 35) + '…' : ev.entityDescription}</span>
                              : '—'}
                          </td>
                          <td style={{ fontSize: 12, color: '#6b7280' }}>{ev.clientName ?? '—'}</td>
                          <td style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{ev.ipAddress ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {actTotalPages > 1 && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginTop: 8 }}>
                  <button className="btn btn-secondary" style={{ fontSize: 12, padding: '5px 12px' }}
                    disabled={actPage <= 1 || actLoading}
                    onClick={() => loadActTimeline(actPage - 1)}>
                    ‹ Previous
                  </button>
                  <span style={{ fontSize: 13, color: '#374151' }}>Page {actPage} / {actTotalPages}</span>
                  <button className="btn btn-secondary" style={{ fontSize: 12, padding: '5px 12px' }}
                    disabled={actPage >= actTotalPages || actLoading}
                    onClick={() => loadActTimeline(actPage + 1)}>
                    Next ›
                  </button>
                </div>
              )}
            </>
          )}

          {/* ── Per-User Summary ── */}
          {actSubView === 'summary' && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px 0', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-primary"
                  style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 5 }}
                  onClick={exportSummaryToExcel}
                  disabled={actExporting || actLoading}
                  title="Download Per-User Summary as Excel"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  {actExporting ? 'Preparing…' : 'Download Summary'}
                </button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Company</th>
                      <th>Status</th>
                      <th>Account Created</th>
                      <th>Last Login</th>
                      <th style={{ textAlign: 'right' }}>Total Logins</th>
                      <th style={{ textAlign: 'right' }}>Logins (30d)</th>
                      <th style={{ textAlign: 'right' }}>Total Actions</th>
                      <th style={{ textAlign: 'right' }}>Actions (30d)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actLoading && (
                      <tr><td colSpan={10} style={{ textAlign: 'center', color: '#6b7280', padding: 24 }}>Loading…</td></tr>
                    )}
                    {!actLoading && actSummary.length === 0 && (
                      <tr><td colSpan={10} style={{ textAlign: 'center', color: '#9ca3af', fontStyle: 'italic', padding: 24 }}>No users found.</td></tr>
                    )}
                    {!actLoading && actSummary.map((u) => (
                      <tr key={u.userId} style={{ fontSize: 13 }}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{u.fullName || u.username}</div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>{u.email}</div>
                        </td>
                        <td>
                          <span style={{
                            padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600,
                            background: u.role === 'SuperUser' || u.role === 'SuperAdmin' ? '#ede9fe' : u.role === 'Admin' ? '#dbeafe' : '#f1f5f9',
                            color:      u.role === 'SuperUser' || u.role === 'SuperAdmin' ? '#4c1d95' : u.role === 'Admin' ? '#1e40af' : '#374151',
                          }}>{formatRole(u.role)}</span>
                        </td>
                        <td style={{ color: '#6b7280' }}>{u.clientName ?? '—'}</td>
                        <td>
                          <span style={{
                            padding: '2px 7px', borderRadius: 9999, fontSize: 11, fontWeight: 600,
                            background: u.isActive ? '#dcfce7' : '#fee2e2',
                            color:      u.isActive ? '#166534'  : '#991b1b',
                          }}>{u.isActive ? 'Active' : 'Inactive'}</span>
                        </td>
                        <td style={{ color: '#6b7280', fontSize: 12 }}>{formatDateBritish(u.createdAt)}</td>
                        <td style={{ color: '#6b7280', fontSize: 12 }}>
                          {u.lastLoginAt ? formatDateTimeBritish(u.lastLoginAt) : <span style={{ color: '#d1d5db' }}>Never</span>}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{u.totalLogins.toLocaleString()}</td>
                        <td style={{ textAlign: 'right', color: u.recentLogins > 0 ? '#002060' : '#9ca3af' }}>{u.recentLogins.toLocaleString()}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{u.totalActions.toLocaleString()}</td>
                        <td style={{ textAlign: 'right', color: u.recentActions > 0 ? '#92400e' : '#9ca3af' }}>{u.recentActions.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: My Feed Management ── */}
      {activeTab === 'feed' && (
        <FeedManagement clients={clients} />
      )}
    </div>
  );
};

export default AdminTools;
